/**
 * Central Appointment Service - Single Source of Truth
 * Manages all appointment data for Calendar, Admin Dashboard, and other components
 */

import AppointmentApiService from './appointmentApiService';
import AppointmentDataService from './appointmentDataService';

class CentralAppointmentService {
  constructor() {
    this.apiService = new AppointmentApiService();
    this.dataService = new AppointmentDataService();
    
    // Import PractitionerService dynamically to avoid circular dependency
    this.practitionerService = null;
    this.initPractitionerService();
    
    // Cache to avoid redundant API calls
    this.cache = {
      appointments: null,
      practitioners: null,
      lastFetch: null,
      cacheExpiry: 5 * 60 * 1000 // 5 minutes cache
    };
  }

  /**
   * Initialize PractitionerService dynamically to avoid circular dependency
   */
  async initPractitionerService() {
    try {
      const PractitionerServiceModule = await import('./practitionerService');
      const PractitionerService = PractitionerServiceModule.default;
      this.practitionerService = new PractitionerService();
    } catch (error) {
      console.error('Failed to load PractitionerService:', error);
    }
  }

  /**
   * Get current calendar year date range
   */
  getCurrentYearRange() {
    const currentYear = new Date().getFullYear();
    const yearStart = new Date(currentYear, 0, 1); // January 1st
    const yearEnd = new Date(currentYear, 11, 31); // December 31st
    
    return {
      startDate: yearStart.toISOString().split('T')[0],
      endDate: yearEnd.toISOString().split('T')[0],
      year: currentYear
    };
  }

  /**
   * Update appointment dates from old years (2023, 2022) to current year (2025) for demo
   */
  updateAppointmentDate(dateString) {
    if (!dateString) return dateString;
    
    try {
      const originalDate = new Date(dateString);
      const year = originalDate.getFullYear();
      
      // Date conversion working correctly
      
      // Convert old years to 2025 for demo purposes
      if (year <= 2023) {
        const yearDiff = 2025 - year;
        const newDate = new Date(originalDate);
        newDate.setFullYear(newDate.getFullYear() + yearDiff);
        
        // Successfully converted to 2025
        
        return newDate.toISOString();
      }
      
      return dateString; // Already current
    } catch (error) {
      console.warn('Error updating appointment date:', error);
      return dateString; // Return original if error
    }
  }

  /**
   * Check if cache is valid
   */
  isCacheValid() {
    return this.cache.lastFetch && 
           this.cache.appointments && 
           (Date.now() - this.cache.lastFetch) < this.cache.cacheExpiry;
  }

  /**
   * Fetch all appointments for current year - SINGLE SOURCE OF TRUTH
   * Priority: 1) Saved Firebase appointments, 2) Backend API, 3) Mock data
   */
  async getAllAppointments(practitioners, forceRefresh = false, userId = null) {
    // Return cached data if valid and practitioners haven't changed
    if (!forceRefresh && this.isCacheValid() && 
        JSON.stringify(practitioners) === JSON.stringify(this.cache.practitioners)) {
      console.log('Using cached appointment data');
      return this.cache.appointments;
    }

    console.log('🔄 Fetching appointment data...');
    
    try {
      let appointments = [];
      
      // 🚀 PRIORITY 1: Try to get saved Firebase appointments first
      if (userId && practitioners && practitioners.length > 0) {
        console.log('📱 Checking for saved appointments in Firebase...');
        
        // Ensure practitioner service is loaded
        if (!this.practitionerService) {
          await this.initPractitionerService();
        }
        
        if (this.practitionerService) {
          const savedAppointments = await this.practitionerService.getSavedAppointments(userId);
        
          if (savedAppointments && savedAppointments.length > 0) {
            console.log(`✅ Found ${savedAppointments.length} saved appointments in Firebase`);
            appointments = this.transformFirebaseAppointments(savedAppointments);
            
            // Cache the results
            this.cache = {
              appointments,
              practitioners: JSON.parse(JSON.stringify(practitioners)),
              lastFetch: Date.now(),
              cacheExpiry: this.cache.cacheExpiry
            };
            
            return appointments;
          } else {
            console.log('📱 No saved appointments found in Firebase');
          }
        } else {
          console.log('❌ PractitionerService failed to load');
        }
      }
      
      // 🔄 PRIORITY 2: Fallback to backend API for real data
      console.log('🌐 Fetching fresh appointment data from backend API');
      const { startDate, endDate } = this.getCurrentYearRange();
      
      const realAppointments = await this.apiService.getAppointmentsForPractitioners(
        practitioners, 
        startDate, 
        endDate
      );

      if (realAppointments && realAppointments.length > 0) {
        console.log(`✅ Retrieved ${realAppointments.length} real appointments from API`);
        // Apply date conversion to Epic appointments for demo purposes
        appointments = realAppointments.map(appointment => {
          const updatedStart = this.updateAppointmentDate(appointment.start);
          const updatedEnd = this.updateAppointmentDate(appointment.end);
          const startDate = updatedStart?.split('T')[0];
          const endDate = updatedEnd?.split('T')[0];
          
          console.log('📅 Appointment Mapping:', {
            originalStart: appointment.start,
            updatedStart: updatedStart,
            extractedStartDate: startDate,
            practitioner: appointment.full_name
          });
          
          return {
            ...appointment,
            start: updatedStart,
            end: updatedEnd,
            start_date: startDate,
            end_date: endDate
          };
        });
      } else {
        console.log('❌ No real appointments found - showing empty state');
        // No fallback data - show empty state when no real data exists
        appointments = [];
      }

      // Cache the results
      this.cache = {
        appointments,
        practitioners: JSON.parse(JSON.stringify(practitioners)), // Deep copy
        lastFetch: Date.now(),
        cacheExpiry: this.cache.cacheExpiry
      };

      return appointments;
    } catch (error) {
      console.error('Error fetching appointments:', error);
      
      // No fallback data - return empty when all methods fail
      console.log('❌ All methods failed, returning empty appointments');
      
      // Cache empty result
      this.cache = {
        appointments: [],
        practitioners: JSON.parse(JSON.stringify(practitioners)),
        lastFetch: Date.now(),
        cacheExpiry: 60 * 1000
      };

      return [];
    }
  }

  /**
   * Transform Firebase appointment data to match expected format
   */
  transformFirebaseAppointments(firebaseAppointments) {
    return firebaseAppointments.map(appointment => {
      // Convert old dates (2023) to current dates (2025) for demo purposes
      const updatedStart = this.updateAppointmentDate(appointment.start);
      const updatedEnd = this.updateAppointmentDate(appointment.end);
      
      const startDate = new Date(updatedStart);
      const endDate = new Date(updatedEnd);
      
      // Extract proper hospital/facility name - handle both old and new formats
      let facilityName = 'Unknown'; // Default when no facility info available
      
      // First try to extract facility from description
      if (appointment.description && appointment.description.includes(' in ')) {
        const parts = appointment.description.split(' in ');
        if (parts.length > 1) {
          const locationPart = parts[1].split(' at ')[0]; // Get "EMH OR"
          if (locationPart && locationPart.trim() !== '') {
            facilityName = locationPart.trim();
          }
        }
      } else if (appointment.location) {
        if (typeof appointment.location === 'string') {
          facilityName = appointment.location;
        } else if (appointment.location.display) {
          facilityName = appointment.location.display;
        } else if (appointment.location.name) {
          facilityName = appointment.location.name;
        }
      } else if (appointment.hospital) {
        if (typeof appointment.hospital === 'string') {
          facilityName = appointment.hospital;
        } else if (appointment.hospital.display) {
          facilityName = appointment.hospital.display;
        } else if (appointment.hospital.name) {
          facilityName = appointment.hospital.name;
        }
      } else if (appointment.organizationName) {
        facilityName = appointment.organizationName;
      }
      
      return {
        id: appointment.id,
        fhir_id: appointment.fhir_id,
        
        // Practitioner info
        full_name: appointment.practitionerName,
        first_name: appointment.practitionerName?.split(' ')[0] || '',
        last_name: appointment.practitionerName?.split(' ')?.slice(1)?.join(' ') || '',
        specialty: appointment.practitionerSpecialty,
        npi: appointment.practitionerNPI,
        physicianId: appointment.practitionerId,
        
        // Appointment details
        procedure: appointment.procedure || appointment.description,
        description: appointment.description,
        status: appointment.status,
        
        // Dates and times (calendar format)
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        start_time: startDate.toTimeString().slice(0, 5),
        end_time: endDate.toTimeString().slice(0, 5),
        
        // Additional metadata - Use proper facility name
        hospital: facilityName,
        location: facilityName,
        priority: appointment.priority || 'routine',
        snomedCode: appointment.snomedCode,
        duration: appointment.duration || 60,
        
        // Source tracking
        source: 'firebase_saved',
        createdAt: appointment.createdAt
      };
    });
  }

  /**
   * Get appointments formatted for FullCalendar (with userId support)
   */
  async getCalendarEvents(practitioners, userId = null) {
    const appointments = await this.getAllAppointments(practitioners, false, userId);
    
    // Calendar events working correctly
    
    return appointments.map(appointment => {
      console.log('📅 FullCalendar Event:', {
        id: appointment.id,
        title: appointment.full_name || appointment.first_name,
        start_date: appointment.start_date,
        start_iso: appointment.start
      });
      
      return {
        id: appointment.id,
        title: appointment.full_name || appointment.first_name,
        start: appointment.start_date, // Use YYYY-MM-DD format for FullCalendar
        extendedProps: {
          ...appointment
        }
      };
    });
  }

  /**
   * Get upcoming appointments (with userId support)
   */
  async getUpcomingAppointments(practitioners, userId = null) {
    const appointments = await this.getAllAppointments(practitioners, false, userId);
    
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    // Debug: Check for upcoming appointments
    console.log('📅 Checking', appointments.length, 'appointments for upcoming ones');
    
    // Filter future appointments
    const futureAppointments = appointments.filter(appointment => {
      const appointmentDate = new Date(appointment.start_date);
      return appointmentDate > today;
    });
    
    if (futureAppointments.length === 0) {
      return [];
    }
    
    // Debug: Log the structure of upcoming appointments
    console.log('📅 Upcoming appointment data structure:', futureAppointments.map(appt => ({
      id: appt.id,
      location: appt.location,
      hospital: appt.hospital,
      organizationName: appt.organizationName,
      procedure: appt.procedure,
      description: appt.description,
      start_date: appt.start_date
    })));
    
    // Group by date
    const appointmentsByDate = {};
    futureAppointments.forEach(appointment => {
      const dateKey = appointment.start_date;
      if (!appointmentsByDate[dateKey]) {
        appointmentsByDate[dateKey] = [];
      }
      appointmentsByDate[dateKey].push(appointment);
    });
    
    // Get earliest date with appointments
    const sortedDates = Object.keys(appointmentsByDate).sort();
    const nextDateWithAppointments = sortedDates[0];
    
    const upcomingAppointments = appointmentsByDate[nextDateWithAppointments].sort((a, b) => {
      return a.start_time.localeCompare(b.start_time);
    });
    
    console.log('📅 Final upcoming appointments for dashboard:', upcomingAppointments);
    return upcomingAppointments;
  }

  /**
   * Get monthly chart data for admin dashboard (with userId support)
   */
  async getMonthlyChartData(practitioners, userId = null) {
    const appointments = await this.getAllAppointments(practitioners, true, userId); // Force refresh for chart
    
    // Debug: Log appointment data for chart
    console.log('📊 Chart processing', appointments.length, 'appointments');
    
    // Check if we have saved appointments (from Firebase)
    if (appointments.length > 0 && appointments[0].source === 'firebase_saved') {
      console.log('📊 Generating chart from saved Firebase appointments');
      const chartData = this.generateMonthlyChartFromSaved(appointments);
      console.log('📊 Chart data generated:', chartData.map(m => ({ month: m.month, total: m.totalAppointments })));
      return chartData;
    }
    
    // Use the API service method if we have real data, otherwise use data service
    if (appointments.length > 0) {
      // Real data only - use API service method
      return this.apiService.generateMonthlyChartFromReal(appointments);
    } else {
      // No appointments - return empty chart data
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return monthNames.map(month => ({
        month,
        totalAppointments: 0,
        breakdown: []
      }));
    }
  }

  /**
   * Generate monthly chart data from saved Firebase appointments (AdminChart format)
   */
  generateMonthlyChartFromSaved(appointments) {
    const currentYear = 2025; // Use 2025 since we convert all dates to 2025
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    console.log('📊 Chart Generation Debug:', {
      appointmentCount: appointments.length,
      targetYear: currentYear,
      appointmentYears: appointments.map(a => new Date(a.start_date).getFullYear())
    });

    // Initialize all months with proper AdminChart format
    const monthlyData = monthNames.map((month, index) => ({
      month,
      year: currentYear,
      monthKey: `${month} ${currentYear}`,
      totalAppointments: 0,
      breakdown: [],
      practitionerMap: new Map() // Temporary for building breakdown
    }));

    // Group appointments by month and practitioner
    appointments.forEach(appointment => {
      const appointmentDate = new Date(appointment.start_date);
      const appointmentYear = appointmentDate.getFullYear();
      
      if (appointmentYear === currentYear) {
        const monthIndex = appointmentDate.getMonth();
        const monthData = monthlyData[monthIndex];
        
        monthData.totalAppointments++;
        
        // Build practitioner breakdown
        const practitionerId = appointment.physicianId || appointment.practitionerId;
        const practitionerName = appointment.full_name;
        
        // Extract proper facility name with multiple fallbacks
        let facilityName = 'Unknown'; // Default when no facility info available
        if (appointment.hospital && appointment.hospital !== 'Hospital') {
          facilityName = appointment.hospital;
        } else if (appointment.location && appointment.location !== 'Hospital') {
          facilityName = appointment.location;
        } else if (appointment.organizationName) {
          facilityName = appointment.organizationName;
        } else if (appointment.specialty) {
          // Generate facility name based on specialty if no other info available
          facilityName = `${appointment.specialty} Center`;
        }
        
        if (!monthData.practitionerMap.has(practitionerId)) {
          monthData.practitionerMap.set(practitionerId, {
            physicianId: practitionerId,
            physicianName: practitionerName,
            specialty: appointment.specialty,
            hospital: facilityName,
            appointmentCount: 0,
            appointments: []
          });
        }
        
        const practitionerData = monthData.practitionerMap.get(practitionerId);
        practitionerData.appointmentCount++;
        practitionerData.appointments.push({
          id: appointment.id,
          date: appointment.start_date,
          startTime: appointment.start_time,
          endTime: appointment.end_time,
          procedure: appointment.procedure,
          description: appointment.description,
          hospital: facilityName
        });
      }
    });

    // Convert practitionerMap to breakdown array and clean up
    return monthlyData.map(data => ({
      month: data.month,
      year: data.year,
      monthKey: data.monthKey,
      totalAppointments: data.totalAppointments,
      breakdown: Array.from(data.practitionerMap.values())
    }));
  }

  /**
   * Get dashboard summary data (with userId support)
   */
  async getDashboardData(practitioners, userId = null) {
    const appointments = await this.getAllAppointments(practitioners, false, userId);
    
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    return practitioners.map(practitioner => {
      const practitionerAppointments = appointments.filter(
        appt => appt.physicianId === practitioner.id || 
                appt.full_name === practitioner.full_name ||
                appt.full_name === practitioner.name ||
                appt.practitionerId === practitioner.id ||
                appt.practitionerNPI === practitioner.npi
      );
      
      const futureAppts = practitionerAppointments.filter(appointment => {
        const appointmentDate = new Date(appointment.start_date);
        return appointmentDate > today;
      });

      // Sort future appointments by date to get the next one
      futureAppts.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

      const nextAppointment = futureAppts.length > 0 
        ? futureAppts[0].start_date 
        : null; // No fake dates - show null when no real appointments exist

      // Extract hospital/facility name from appointments or practitioner data
      let facilityName = 'Unknown'; // Default when no facility info available
      
      // Try to get facility from most recent appointment first
      if (practitionerAppointments.length > 0) {
        const recentAppt = practitionerAppointments[practitionerAppointments.length - 1];
        // First check for proper facility name from description 
        if (recentAppt.description && recentAppt.description.includes(' in ')) {
          // Extract facility from description like "APPENDECTOMY [...] in EMH OR at..."
          const parts = recentAppt.description.split(' in ');
          if (parts.length > 1) {
            const locationPart = parts[1].split(' at ')[0]; // Get "EMH OR"
            if (locationPart && locationPart.trim() !== '') {
              facilityName = locationPart.trim();
            }
          }
        } else if (recentAppt.hospital && recentAppt.hospital !== 'Hospital') {
          // Handle both string and object formats
          if (typeof recentAppt.hospital === 'string') {
            facilityName = recentAppt.hospital;
          } else if (recentAppt.hospital.display) {
            facilityName = recentAppt.hospital.display;
          } else if (recentAppt.hospital.name) {
            facilityName = recentAppt.hospital.name;
          }
        } else if (recentAppt.location && recentAppt.location !== 'Hospital') {
          // Handle both string and object formats
          if (typeof recentAppt.location === 'string') {
            facilityName = recentAppt.location;
          } else if (recentAppt.location.display) {
            facilityName = recentAppt.location.display;
          } else if (recentAppt.location.name) {
            facilityName = recentAppt.location.name;
          }
        } else if (recentAppt.organizationName) {
          facilityName = recentAppt.organizationName;
        }
      }
      
      // Fallback to practitioner data if no appointment facility found
      if (facilityName === 'Unknown') {
        if (practitioner.hospital && practitioner.hospital !== 'Hospital') {
          // Handle both string and object formats
          if (typeof practitioner.hospital === 'string') {
            facilityName = practitioner.hospital;
          } else if (practitioner.hospital.display) {
            facilityName = practitioner.hospital.display;
          } else if (practitioner.hospital.name) {
            facilityName = practitioner.hospital.name;
          }
        } else if (practitioner.organization) {
          facilityName = practitioner.organization;
        } else if (practitioner.specialty) {
          facilityName = `${practitioner.specialty} Center`;
        }
      }

      return {
        id: practitioner.id,
        full_name: practitioner.full_name || practitioner.name,
        specialty: practitioner.specialty,
        hospital: facilityName,
        cases_completed: practitionerAppointments.length,
        next_appointment: nextAppointment,
        npi: practitioner.npi,
        originalData: practitioner
      };
    });
  }

  /**
   * Clear cache (useful for refresh operations)
   */
  clearCache() {
    this.cache = {
      appointments: null,
      practitioners: null,
      lastFetch: null,
      cacheExpiry: this.cache.cacheExpiry
    };
  }

  /**
   * Force refresh data
   */
  async refreshData(practitioners) {
    this.clearCache();
    return await this.getAllAppointments(practitioners, true);
  }

  /**
   * Force clear cache and refresh immediately (for development/testing)
   */
  forceClearCache() {
    console.log('🔄 Forcing cache clear - next request will fetch fresh data');
    this.clearCache();
  }
}

// Create and export singleton instance
const centralAppointmentService = new CentralAppointmentService();
export default centralAppointmentService; 